import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchOrder,
  OrderResponse,
  updateOrder,
  type UpdateOrderPayload,
} from '../../../api/orders';
import Notification from '../../../components/midone/components/Base/Notification';
import type { NotificationElement } from '../../../components/midone/components/Base/Notification';
import { type UserProfile } from '../../../api/auth';
import ServiceAssignmentsTable from './ServiceAssignmentsTable';
import { useServiceAssignments } from '../hooks/useServiceAssignments';
// global technician UI removed

interface OrderAssignTechnicianProps {
  orderId: string | null;
  showHeader?: boolean;
  onBackToOrders?: () => void;
  onReturnToIntake?: (orderId: string | null) => void;
  onCompleted?: (orderId: string) => void;
  onOrderUpdate?: (order: OrderResponse) => void;
}

type ToastState = {
  id: number;
  title: string;
  message: string;
};

const TOAST_DURATION = 2500;

const OrderAssignTechnician: FC<OrderAssignTechnicianProps> = ({
  orderId,
  showHeader = true,
  onCompleted,
  onOrderUpdate,
}) => {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  // Global technician assignment removed; only per-service assignments remain
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastRef = useRef<NotificationElement | null>(null);

  const {
    assignments: serviceAssignments,
    setAssignmentTechnician,
    updateAssignment,
    dirty: serviceAssignmentsDirty,
    payload: serviceAssignmentPayload,
    serviceItems,
  } = useServiceAssignments(order);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setOrderError('Order ID not specified.');
      setLoadingOrder(false);
      return;
    }

    let isMounted = true;
    setLoadingOrder(true);
    setOrderError(null);

    fetchOrder(orderId)
      .then((data) => {
        if (!isMounted) {
          return;
        }
        setOrder(data);
        setSaveError(null);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load order details.';
        setOrder(null);
        setOrderError(message);
      })
      .finally(() => {
        if (isMounted) {
          setLoadingOrder(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [orderId]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    toastRef.current?.showToast?.();

    const handle = window.setTimeout(() => {
      toastRef.current?.hideToast?.();
      setToast(null);
    }, TOAST_DURATION);

    return () => {
      window.clearTimeout(handle);
      toastRef.current?.hideToast?.();
    };
  }, [toast]);

  const allAssigned = useMemo(
    () => (serviceItems.length === 0) || serviceAssignmentPayload.every((e) => !!e.technicianId),
    [serviceItems.length, serviceAssignmentPayload],
  );
  // Enable save when: all service items have a technician OR there are no service items (to progress status)
  const disableSave = useMemo(
    () => saving || !((serviceItems.length === 0) || (allAssigned && serviceAssignmentsDirty)),
    [saving, serviceItems.length, allAssigned, serviceAssignmentsDirty],
  );

  const nextStatusBanner = useMemo(() => {
    if (serviceItems.length === 0) {
      return {
        tone: 'info',
        text: 'No services linked. Saving will set status to In progress.',
      } as const;
    }
    if (serviceAssignmentPayload.length > 0 && serviceAssignmentPayload.every((e) => !!e.technicianId)) {
      return {
        tone: 'success',
        text: 'All services have a technician. Saving will set status to In progress.',
      } as const;
    }
    return {
      tone: 'muted',
      text: 'Assign a technician to each service to enable progressing to In progress.',
    } as const;
  }, [serviceItems.length, serviceAssignmentPayload]);

  const handleSave = async () => {
    if (!orderId) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload: UpdateOrderPayload = serviceItems.length
      ? { serviceAssignments: serviceAssignmentPayload }
      : {}; // allow status progression without global techs

    try {
      const updated = await updateOrder(orderId, payload);
      setOrder(updated);
      onOrderUpdate?.(updated);
      setToast({
        id: Date.now(),
        title: 'Saved',
        message: 'Technician assignments have been updated.',
      });
      if (onCompleted) {
        onCompleted(updated.id);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message || 'Failed to update assigned technicians.'
          : 'Failed to update assigned technicians.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Notification
        className="flex w-80 max-w-full flex-col items-center gap-1 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-center text-primary shadow-lg"
        options={{
          duration: TOAST_DURATION,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true,
          close: false,
          offset: {
            x: 24,
            y: 24,
          },
        }}
        getRef={(el: NotificationElement) => {
          toastRef.current = el;
        }}
      >
        {toast ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">{toast.title}</p>
            <p className="text-xs text-primary/80">{toast.message}</p>
          </div>
        ) : null}
      </Notification>

      {showHeader ? (
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-slate-800">Technician assignment</h2>
          <p className="text-sm text-slate-500">Assign technicians to each service line. Global technician assignment has been removed.</p>
        </div>
      ) : null}

      {orderError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">{orderError}</div>
      ) : null}

      <div className="rounded-2xl  bg-white p-6 shadow-sm">
        {loadingOrder ? (
          <p className="text-sm text-slate-500">Loading order details…</p>
        ) : !order ? (
          <p className="text-sm text-slate-500">Select an order to assign technicians.</p>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Order number</p>
                <p className="text-lg font-semibold text-slate-800">{order.orderNumber}</p>
              </div>
            </div>

            <div className="space-y-3">
              <ServiceAssignmentsTable
                order={order}
                assignments={serviceAssignments}
                onChange={setAssignmentTechnician}
                onBayChange={(orderItemId, bayId) => updateAssignment(orderItemId, { bayId })}
                onNotesChange={(orderItemId, notes) => updateAssignment(orderItemId, { notes })}
                onStartTimeChange={(orderItemId, startTime) => updateAssignment(orderItemId, { startTime })}
                onEndTimeChange={(orderItemId, endTime) => updateAssignment(orderItemId, { endTime })}
                disabled={saving}
              />
            </div>

            <div
              className={`rounded-xl px-4 py-3 text-xs ${nextStatusBanner.tone === 'success'
                  ? 'border border-emerald-300/60 bg-emerald-50 text-emerald-700'
                  : nextStatusBanner.tone === 'info'
                    ? 'border border-sky-300/60 bg-sky-50 text-sky-700'
                    : 'border border-slate-200 bg-slate-50 text-slate-600'
                }`}
            >
              {nextStatusBanner.text}
            </div>

            {saveError ? (
              <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{saveError}</div>
            ) : null}

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleSave}
                disabled={disableSave}
              >
                {saving ? 'Saving…' : 'Save assignments'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderAssignTechnician;
